import { NavLink, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';
import NotificacionCampana from './NotificacionCampana';
import MailCampana from './MailCampana';
import NotificacionCampanaANSES from './NotificacionCampanaANSES';
import ChatInternoPanel from './ChatInternoPanel';
import chatInternoService from '../services/chatInternoService';
import useNotificacionesWhatsApp from '../hooks/useNotificacionesWhatsApp';
import { useAuth } from '../auth/AuthContext';
import { etiquetaRol } from '../auth/roles';
import AvatarUsuario from './ui/AvatarUsuario';
import { FaUserCircle } from 'react-icons/fa';
import {
    getSeccionesVisibles,
    getGruposVisibles,
    seccionTieneRutaActiva,
} from '../config/menuSections';
import '../style/components/NavegacionLateral.css';

const RAIL_VISIBLE_KEY = 'nav_rail_visible';
const RAIL_ANCHO = '128px';
const RAIL_ANCHO_OCULTO = '0px';

function Navegacion({ hideNotifications = false, showDrawer = true }) {
    const location = useLocation();
    const [grupoAbierto, setGrupoAbierto] = useState(null);
    const [railVisible, setRailVisible] = useState(() => localStorage.getItem(RAIL_VISIBLE_KEY) === '1');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [chatInternoOpen, setChatInternoOpen] = useState(false);
    const [, setTotalUnreadChat] = useState(0);
    const { user, logout, puede } = useAuth();
    const { totalNoLeidos: totalNoLeidosWhatsapp, abrirPanel: abrirPanelWhatsapp } = useNotificacionesWhatsApp();

    const refrescarTotalUnreadChat = useCallback(() => {
        if (!user) return;
        chatInternoService.getTotalNoLeidos().then(setTotalUnreadChat).catch(() => setTotalUnreadChat(0));
    }, [user]);

    useEffect(() => {
        if (!user) return;
        refrescarTotalUnreadChat();
    }, [user, refrescarTotalUnreadChat]);

    useEffect(() => {
        if (!chatInternoOpen && user) refrescarTotalUnreadChat();
    }, [chatInternoOpen, user, refrescarTotalUnreadChat]);

    const profileRef = useRef(null);
    const railRef = useRef(null);

    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
    const toggleGrupo = (id) => setGrupoAbierto((prev) => (prev === id ? null : id));
    const cerrarFlyout = () => setGrupoAbierto(null);
    const toggleRailVisible = () => {
        setRailVisible((prev) => !prev);
        setGrupoAbierto(null);
    };

    // Ancho real del riel como variable CSS: la lee tanto el propio riel como
    // .main-layout (MainLayout.js) para reservar el espacio correcto — ver el
    // comentario en NavegacionLateral.css sobre por qué va como padding ahí y
    // no como margin en .container.
    useEffect(() => {
        localStorage.setItem(RAIL_VISIBLE_KEY, railVisible ? '1' : '0');
        document.documentElement.style.setProperty('--nav-rail-width', railVisible ? RAIL_ANCHO : RAIL_ANCHO_OCULTO);
        // Bandera aparte del ancho: en mobile el riel mide 64px y no 128px, así que el CSS no
        // puede deducir de --nav-rail-width si está abierto. Con esto el breakpoint calcula su
        // propio ancho (64px * bandera) sin duplicar el estado en JS.
        document.documentElement.style.setProperty('--nav-rail-abierto', railVisible ? '1' : '0');
    }, [railVisible]);

    // menuSections/GRUPOS_NAV y su filtrado por permiso viven en config/menuSections.js,
    // compartidos con la navegación mobile (ver components/mobile/NavegacionMobile.jsx).
    const seccionesVisibles = getSeccionesVisibles(user, puede);

    const seccionClientes = seccionesVisibles.find((section) => section.id === 'clientes');
    const seccionTramites = seccionesVisibles.find((section) => section.id === 'tramites');

    const tieneItemActivo = useCallback(
        (section) => seccionTieneRutaActiva(location.pathname, section),
        [location.pathname]
    );

    // Cada grupo del riel solo se muestra si al menos una de sus secciones miembro
    // todavía tiene items visibles para el rol actual (reusa el filtro por rol de arriba).
    const gruposVisibles = getGruposVisibles(seccionesVisibles);

    const grupoTieneActivo = useCallback(
        (grupo) => grupo.secciones.some(tieneItemActivo),
        [tieneItemActivo]
    );

    const grupoActual = gruposVisibles.find((g) => g.id === grupoAbierto) || null;

    // Cierra el flyout al clickear afuera del riel/flyout (mismo patrón que el dropdown de usuario).
    useEffect(() => {
        const handleClickOutsideNav = (event) => {
            if (railRef.current && !railRef.current.contains(event.target)) {
                setGrupoAbierto(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideNav);
        return () => document.removeEventListener('mousedown', handleClickOutsideNav);
    }, []);

    const renderItemsSeccion = (section) => (
        <div key={section.id} className="nav-flyout-section">
            <div className="nav-section-subtitle">
                <i className={`${section.icon} nav-section-subtitle-icon`} /> {section.title}
            </div>
            <ul className="side-menu-list">
                {section.items.map((item) => (
                    <li key={item.to}>
                        <NavLink to={item.to} onClick={cerrarFlyout} className={({ isActive }) => `nav-menu-item ${isActive ? 'active' : ''}`}>
                            <i className={`${item.icon} nav-item-icon`} />
                            <span>{item.label}</span>
                        </NavLink>
                    </li>
                ))}
            </ul>

            {section.reportesItems?.length > 0 && (
                <>
                    <div className="nav-section-subtitle">
                        <i className="bi bi-bar-chart-fill nav-section-subtitle-icon" /> REPORTES
                    </div>
                    <ul className="side-menu-list">
                        {section.reportesItems.map((item) => (
                            <li key={item.to}>
                                <NavLink to={item.to} onClick={cerrarFlyout} className={({ isActive }) => `nav-menu-item ${isActive ? 'active' : ''}`}>
                                    <i className={`${item.icon} nav-item-icon`} />
                                    <span>{item.label}</span>
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <>
            <nav className="navbar navbar-dark shadow-sm d-flex justify-content-between align-items-center px-4">
                <div className="navbar-left d-flex align-items-center">
                    <NavLink to="/home" className="navbar-brand fw-bold mb-0 d-flex align-items-center" style={{ textDecoration: 'none' }}>
                        <span className="nav-brand-full">Estudio Jurídico Previsional</span>
                        <span className="nav-brand-short">GestiónEJP</span>
                    </NavLink>
                </div>

                <div className="navbar-right d-flex align-items-center gap-3">
                    {!hideNotifications && user && (
                        <>
                            <div className="notificacion-campana-icono">
                                <NotificacionCampana idUsuario={user.id} />
                            </div>
                            <div className="notificacion-campana-icono">
                                <MailCampana />
                            </div>
                            {/* Mismos roles que ven "Seguimiento de Expedientes" en el menú (grupo Herramientas). */}
                            {puede('herramientas.expedientes') && (
                            <div className="notificacion-campana-icono">
                                <NotificacionCampanaANSES />
                            </div>
                            )}
                            <button
                                className="btn position-relative p-0 border-0 d-flex align-items-center justify-content-center whatsapp-notificacion-icono"
                                title="Notificaciones de WhatsApp"
                                onClick={abrirPanelWhatsapp}
                            >
                                <i
                                    className={`bi bi-whatsapp ${
                                        totalNoLeidosWhatsapp > 0 ? 'text-success shake-whatsapp' : 'text-light'
                                    }`}
                                    style={{
                                        fontSize: '39px',
                                        transition: 'transform 0.25s ease, color 0.25s ease',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.15)')}
                                    onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                />
                                {totalNoLeidosWhatsapp > 0 && (
                                    <span
                                        className="position-absolute rounded-circle"
                                        style={{ width: '14px', height: '14px', top: '16%', right: '12%', backgroundColor: '#25D366' }}
                                    />
                                )}
                                {totalNoLeidosWhatsapp > 0 && (
                                    <span
                                        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-success text-white"
                                        style={{ fontSize: '0.9rem', minWidth: '28px', padding: '0.35rem 0.5rem' }}
                                    >
                                        {totalNoLeidosWhatsapp > 99 ? '99+' : totalNoLeidosWhatsapp}
                                    </span>
                                )}
                            </button>
                        </>
                    )}

                    {user ? (
                        <div className="user-profile" ref={profileRef}>
                            <div className="user-menu" onClick={toggleDropdown}>
                                <div className="user-avatar">
                                    <AvatarUsuario nombre={user.nombre} foto={user.fotoPerfil} size={40} />
                                </div>
                                <div className="user-info">
                                    <span className="user-name">{user.nombre}</span>
                                    <span className="user-role">{etiquetaRol(user.rol)}</span>
                                </div>
                                <i className={`bi bi-chevron-${dropdownOpen ? 'up' : 'down'} ms-2 user-menu-chevron`}></i>
                            </div>
                        </div>
                    ) : (
                        <NavLink to="/login" className="user-menu-login" style={{ textDecoration: 'none' }}>
                            <FaUserCircle className="user-icon" />
                            <span className="user-name">Iniciar Sesión</span>
                        </NavLink>
                    )}
                </div>
            </nav>

            {user && <ChatInternoPanel isOpen={chatInternoOpen} onClose={() => setChatInternoOpen(false)} onUnreadChange={refrescarTotalUnreadChat} />}

            {user && dropdownOpen && (
                <div className="dropdown-menu-container" ref={profileRef}>
                    <div className="dropdown-menu-modern show">
                        <div className="dropdown-header">
                            <div className="dropdown-user-info">
                                <AvatarUsuario nombre={user.nombre} foto={user.fotoPerfil} size={48} />
                                <div>
                                    <div className="dropdown-user-name" style={{color: "white"}}>{user.nombre}</div>
                                    <div className="dropdown-user-email">{user.username}</div>
                                </div>
                            </div>
                        </div>
                        <div className="dropdown-divider"></div>
                        <NavLink to="/mi-perfil" className="dropdown-item-modern" onClick={() => setDropdownOpen(false)}>
                            <i className="bi bi-person-circle me-3"></i>
                            <span>Mi Perfil</span>
                        </NavLink>
                        <button onClick={logout} className="dropdown-item-modern logout-item">
                            <i className="bi bi-box-arrow-right me-3"></i>
                            <span>Cerrar Sesión</span>
                        </button>
                    </div>
                </div>
            )}

            {showDrawer && (
                <div className="nav-rail-wrapper" ref={railRef}>
                    <button
                        type="button"
                        className={`nav-rail-toggle-btn ${railVisible ? 'open' : ''}`}
                        onClick={toggleRailVisible}
                        title={railVisible ? 'Contraer menú' : 'Expandir menú'}
                    >
                        <i className={`bi bi-chevron-${railVisible ? 'left' : 'right'}`} />
                        <span className="nav-rail-toggle-etiqueta">{railVisible ? 'CERRAR' : 'MENÚ'}</span>
                    </button>

                    <div className={`nav-rail ${!railVisible ? 'oculto' : ''}`}>
                        {seccionClientes?.items?.[0] && (
                            <NavLink
                                to={seccionClientes.items[0].to}
                                onClick={cerrarFlyout}
                                className={({ isActive }) => `nav-rail-icon ${isActive ? 'active' : ''}`}
                                title="Clientes"
                            >
                                <i className={seccionClientes.icon} />
                                <span className="nav-rail-label">Clientes</span>
                            </NavLink>
                        )}

                        {seccionTramites?.items?.[0] && (
                            <NavLink
                                to={seccionTramites.items[0].to}
                                onClick={cerrarFlyout}
                                className={({ isActive }) => `nav-rail-icon ${isActive ? 'active' : ''}`}
                                title="Trámites"
                            >
                                <i className={seccionTramites.icon} />
                                <span className="nav-rail-label">Trámites</span>
                            </NavLink>
                        )}

                        {gruposVisibles.length > 0 && (seccionClientes || seccionTramites) && <div className="nav-rail-divider" />}

                        {gruposVisibles.map((grupo) => (
                            <button
                                key={grupo.id}
                                type="button"
                                className={`nav-rail-icon ${grupoAbierto === grupo.id ? 'open' : ''} ${grupoTieneActivo(grupo) ? 'active' : ''}`}
                                onClick={() => toggleGrupo(grupo.id)}
                                aria-expanded={grupoAbierto === grupo.id}
                                title={grupo.title}
                            >
                                <i className={grupo.icon} />
                                <span className="nav-rail-label">{grupo.title}</span>
                            </button>
                        ))}
                    </div>

                    {grupoActual && (
                        <div className="nav-flyout">
                            <div className="nav-flyout-header">
                                <i className={`${grupoActual.icon} me-2`} />
                                {grupoActual.title}
                            </div>
                            <div className="nav-flyout-body">
                                {grupoActual.secciones.map(renderItemsSeccion)}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default Navegacion;
